/**
 * AI value validation — the composition point for the table, the format
 * interpreter and the check-digit algorithm.
 *
 * Ordering matters and matches spec §3.2 step 1: charset and length first, then
 * the check digit. Checking a check digit before knowing the value is numeric
 * and the right length produces a `CHECK_DIGIT` error for what is really a
 * charset problem, and that is the message that reaches a user staring at a
 * spreadsheet cell.
 */
import { formatFor, PATH_AIS } from "./ai.js";
import { hasValidCheckDigit } from "./check-digit.js";
import { GS1Error } from "./errors.js";
import { validateFormat } from "./format.js";
/**
 * Validate one AI value as it appears in a Digital Link path.
 *
 * **GTIN callers:** this is strict — AI 01 is `N14` per the specification, so a
 * 12- or 13-digit retail form fails on length. Normalise with
 * `normalizeToGtin14` first; spec §3.2 lists zero-padding as part of parsing,
 * before validation, for exactly this reason.
 *
 * @throws {GS1Error} `UNKNOWN_AI`, `LENGTH`, `CHARSET` or `CHECK_DIGIT`
 */
export function validateAIValue(ai, value) {
    const definition = PATH_AIS[ai];
    const format = formatFor(ai);
    if (definition === undefined || format === undefined) {
        throw new GS1Error("UNKNOWN_AI", `AI ${JSON.stringify(ai)} is not a Digital Link path AI.`, ai);
    }
    validateFormat(value, format, ai);
    const { checkDigitLength } = definition;
    if (checkDigitLength !== undefined &&
        !hasValidCheckDigit(value.slice(0, checkDigitLength))) {
        throw new GS1Error("CHECK_DIGIT", `AI ${ai} (${definition.label}) value ${value} has an incorrect check digit.`, ai);
    }
}
/**
 * Non-throwing form, for callers collecting every problem in a batch import
 * rather than failing on the first.
 */
export function checkAIValue(ai, value) {
    try {
        validateAIValue(ai, value);
        return null;
    }
    catch (error) {
        if (error instanceof GS1Error)
            return error;
        throw error;
    }
}
//# sourceMappingURL=validate.js.map